// SHOW THE MATH (#3365) — governed derivation builders.
//
// Pure, deterministic helpers that compose a governed number's derivation the
// way a senior partner would explain it: a build-up WATERFALL (total pool spend
// → addressable → savings rate → recovery → net), input provenance for every
// operand, current-vs-benchmark comparisons where a rate is priced to a
// benchmark, and one sanity-anchor line tying the result to the client's own
// demonstrated pace. Every value is a pre-formatted governed display string —
// this module does NO arithmetic that mints a governed figure; each stage's
// running value and the net result are passed in already computed by the
// approved formula runtime and shown verbatim.
//
// FOUNDER RULING 2026-07-18: savings rates apply to the ADDRESSABLE base, not
// total spend. The base phrase reflects that; the waterfall carves total → addressable
// with the exclusion logic named before the rate is applied.

import type {
  AskHelixNumericDerivationBenchmarkDto,
  AskHelixNumericDerivationDto,
  AskHelixNumericDerivationOperandDto,
  AskHelixNumericDerivationStageDto,
} from '../../ask-helix';

// The shared-services savings-rate base wording. FOUNDER RULING 2026-07-18:
// rates apply to the ADDRESSABLE base. The phrase lives behind this single
// constant so a future ruling changes ONE string — not the formula, the
// builder, or the renderer.
export const SGA_SHARED_SERVICES_RATE_BASE_PHRASE =
  'applied to the addressable base, not total pool spend';

export function formatDerivationUsd(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${trimZeros((value / 1_000_000_000).toFixed(1))}B`;
  if (abs >= 1_000_000) return `$${trimZeros((value / 1_000_000).toFixed(1))}M`;
  if (abs >= 1_000) return `$${trimZeros((value / 1_000).toFixed(1))}K`;
  return `$${Math.round(value)}`;
}

export function formatDerivationPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${trimZeros(value.toFixed(2))}%`;
}

/**
 * Compose the human formula string from ordered operands and a result. Each
 * operand contributes its leading operator (if any) and value; the result is
 * appended after " = ". Operators are drawn from the operands themselves so the
 * string always matches the structured rows the drawer renders.
 */
export function composeDerivationExpression(
  operands: readonly AskHelixNumericDerivationOperandDto[],
  resultValue: string,
): string {
  const body = operands
    .filter((operand) => operand.role !== 'result')
    .map((operand, index) =>
      index === 0 || !operand.operator
        ? operand.value
        : `${operand.operator} ${operand.value}`,
    )
    .join(' ');
  return `${body} = ${resultValue}`;
}

export type SgaSharedServicesDerivationInput = Readonly<{
  leverLabel: string;
  spendNoun: string;
  // Governed anchor values (already computed by the approved formula runtime).
  totalSpendUsd: number; // stage 1 — total pool spend
  addressableSpendUsd: number; // stage 2 — addressable base (rate applies here)
  grossSavingsUsd: number; // stage 3 running — addressable × savings rate
  grossRecoveryUsd: number; // stage 4 running — × recovery factor, pre-program-cost
  opportunityUsd: number; // stage 5 — net operating-income opportunity
  savingsRatePct: number;
  recoveryFactorPct: number;
  programCostUsd: number;
  // What is carved out of total spend to reach the addressable base, and why.
  addressableExclusionNote: string;
  // Benchmark range the savings rate is anchored to (e.g. 5-9%), chosen point.
  savingsRateBand?: { lowPct: number; highPct: number } | null;
  source: { label: string; asOf?: string | null; sourceClassLabel?: string | null };
  // One sanity-anchor line tying the result to the client's demonstrated pace.
  sanityAnchor?: string | null;
  // Overrides the default rate-base phrase (constant) when a ruling lands.
  rateBasePhrase?: string;
}>;

/**
 * SG&A cost-pool opportunity, addressable-base build-up: total pool spend →
 * addressable (exclusions named) → savings rate (benchmark-anchored) → recovery
 * factor → net opportunity. The demo-burning shared-services case, generalised
 * to any SG&A cost-pool lever. Returns null when any governed input is
 * non-finite (honest withhold — never a fabricated formula).
 */
export function buildSgaSharedServicesDerivation(
  input: SgaSharedServicesDerivationInput,
): AskHelixNumericDerivationDto | null {
  const numeric = [
    input.totalSpendUsd,
    input.addressableSpendUsd,
    input.grossSavingsUsd,
    input.grossRecoveryUsd,
    input.opportunityUsd,
    input.savingsRatePct,
    input.recoveryFactorPct,
    input.programCostUsd,
  ];
  if (numeric.some((value) => !Number.isFinite(value))) return null;

  const source = normalizeSource(input.source);
  const totalSpend = formatDerivationUsd(input.totalSpendUsd);
  const addressable = formatDerivationUsd(input.addressableSpendUsd);
  const savingsRate = formatDerivationPct(input.savingsRatePct);
  const grossSavings = formatDerivationUsd(input.grossSavingsUsd);
  const recovery = formatDerivationPct(input.recoveryFactorPct);
  const grossRecovery = formatDerivationUsd(input.grossRecoveryUsd);
  const programCost = formatDerivationUsd(input.programCostUsd);
  const opportunity = formatDerivationUsd(input.opportunityUsd);
  const basePhrase = input.rateBasePhrase ?? SGA_SHARED_SERVICES_RATE_BASE_PHRASE;
  const band = input.savingsRateBand
    ? {
        low: formatDerivationPct(input.savingsRateBand.lowPct),
        high: formatDerivationPct(input.savingsRateBand.highPct),
        chosen: savingsRate,
      }
    : null;

  const stages: AskHelixNumericDerivationStageDto[] = [
    {
      id: 'total_pool_spend',
      label: 'Total pool spend',
      kind: 'base',
      runningValue: totalSpend,
      source,
    },
    {
      id: 'addressable_base',
      label: 'Addressable base',
      kind: 'carve_out',
      runningValue: addressable,
      transform: 'carve out non-addressable spend',
      note: input.addressableExclusionNote,
      source,
    },
    {
      id: 'savings_rate_applied',
      label: 'Savings rate applied',
      kind: 'rate',
      runningValue: grossSavings,
      transform: `× ${savingsRate}`,
      note: `Savings rate ${basePhrase}.`,
      band,
      source,
    },
    {
      id: 'recovery_factor_applied',
      label: 'Recovery factor applied',
      kind: 'factor',
      runningValue: grossRecovery,
      transform: `× ${recovery}`,
      note: 'Approved share of gross savings booked as operating income.',
      source,
    },
    {
      id: 'net_opportunity',
      label: 'Net opportunity',
      kind: 'result',
      runningValue: opportunity,
      transform: `− ${programCost} program cost`,
      source,
    },
  ];

  const operands: AskHelixNumericDerivationOperandDto[] = [
    {
      role: 'base',
      label: `Addressable ${input.spendNoun}`,
      value: addressable,
      source,
      baseClarity: `Carved from ${totalSpend} total pool spend — ${input.addressableExclusionNote}`,
    },
    {
      role: 'rate',
      label: 'Approved savings rate',
      value: savingsRate,
      operator: '×',
      source,
      baseClarity: basePhrase,
    },
    {
      role: 'factor',
      label: 'Recovery factor',
      value: recovery,
      operator: '×',
      source,
    },
    {
      role: 'cost',
      label: 'Program cost',
      value: programCost,
      operator: '−',
      source,
    },
  ];

  return {
    expression: `${addressable} addressable × ${savingsRate} × ${recovery} − ${programCost} program cost = ${opportunity}`,
    resultLabel: `${input.leverLabel} operating-income opportunity`,
    resultValue: opportunity,
    operands,
    stages,
    sanityAnchor: input.sanityAnchor ?? null,
    // #4008 wave-1 class B2 — "…booked as operating income, net of program cost"
    // read as though the recovery factor were ALREADY net of the program cost,
    // which is the ambiguity `techsw-1` narrated into "76% of gross savings
    // converts to operating income after a program cost of $1,750,000" (net /
    // gross on that run is 87.1%, not 76%). The stages above are unambiguous —
    // `recovery_factor_applied` (× recovery) precedes `net_opportunity`
    // (− programCost) — so the note now states that order in the same words.
    basisNote:
      `Savings rate ${basePhrase}; recovery factor is the approved share of gross ` +
      'savings booked as operating income, applied before the program cost is ' +
      'subtracted.',
  };
}

export type RiskClaimsLossReductionDerivationInput = Readonly<{
  currentLossRatioPct: number;
  benchmarkLossRatioPct: number;
  freightRevenueUsd: number;
  opportunityUsd: number;
  benchmarkSourceLabel: string;
  benchmarkAsOf?: string | null;
  // Benchmark band the loss-ratio floor sits in (e.g. 1.0-3.0%), when known.
  benchmarkBand?: { lowPct: number; highPct: number } | null;
  source: { label: string; asOf?: string | null; sourceClassLabel?: string | null };
  sanityAnchor?: string | null;
}>;

/**
 * Risk & claims loss-reduction recovery: (current loss ratio − benchmark loss
 * ratio) × freight revenue, priced to the industry benchmark. Carries the
 * current-vs-benchmark comparison + rationale + source on the loss-ratio
 * operand, and a build-up waterfall (current → priced-to-benchmark gap →
 * × freight revenue → recovery). Returns null when any input is non-finite.
 */
export function buildRiskClaimsLossReductionDerivation(
  input: RiskClaimsLossReductionDerivationInput,
): AskHelixNumericDerivationDto | null {
  const numeric = [
    input.currentLossRatioPct,
    input.benchmarkLossRatioPct,
    input.freightRevenueUsd,
    input.opportunityUsd,
  ];
  if (numeric.some((value) => !Number.isFinite(value))) return null;

  const source = normalizeSource(input.source);
  const current = formatDerivationPct(input.currentLossRatioPct);
  const benchmark = formatDerivationPct(input.benchmarkLossRatioPct);
  const gapPts = formatDerivationPct(input.currentLossRatioPct - input.benchmarkLossRatioPct);
  const freightRevenue = formatDerivationUsd(input.freightRevenueUsd);
  const opportunity = formatDerivationUsd(input.opportunityUsd);
  const band = input.benchmarkBand
    ? {
        low: formatDerivationPct(input.benchmarkBand.lowPct),
        high: formatDerivationPct(input.benchmarkBand.highPct),
        chosen: benchmark,
      }
    : null;

  const benchmarkComparison: AskHelixNumericDerivationBenchmarkDto = {
    currentLabel: 'Yours',
    currentValue: current,
    benchmarkLabel: 'Industry benchmark',
    benchmarkValue: benchmark,
    rationale: 'Priced to the industry best-in-class loss-ratio floor.',
    source: input.benchmarkSourceLabel,
    asOf: input.benchmarkAsOf ?? null,
  };

  const stages: AskHelixNumericDerivationStageDto[] = [
    {
      id: 'current_loss_ratio',
      label: 'Current loss ratio',
      kind: 'base',
      runningValue: current,
      note: 'Incurred claims ÷ freight revenue for the approved reporting cut.',
      source,
    },
    {
      id: 'priced_to_benchmark',
      label: 'Priced to benchmark',
      kind: 'carve_out',
      runningValue: gapPts,
      transform: `− ${benchmark} benchmark floor`,
      note: `${benchmarkComparison.rationale} Source: ${input.benchmarkSourceLabel}.`,
      band,
      source,
    },
    {
      id: 'applied_to_freight_revenue',
      label: 'Applied to freight revenue',
      kind: 'result',
      runningValue: opportunity,
      transform: `× ${freightRevenue} freight revenue`,
      note: 'Freight revenue reconciled to the finance P&L (loss-ratio denominator).',
      source,
    },
  ];

  const operands: AskHelixNumericDerivationOperandDto[] = [
    {
      role: 'base',
      label: 'Loss-ratio gap to benchmark',
      value: `${current} − ${benchmark} = ${gapPts}`,
      source,
      benchmark: benchmarkComparison,
    },
    {
      role: 'factor',
      label: 'Freight revenue',
      value: freightRevenue,
      operator: '×',
      source,
      baseClarity: 'Freight revenue reconciled to the finance P&L (loss-ratio denominator).',
    },
  ];

  return {
    expression: `(${current} − ${benchmark}) × ${freightRevenue} = ${opportunity}`,
    resultLabel: 'Claims loss-reduction operating-income recovery',
    resultValue: opportunity,
    operands,
    stages,
    sanityAnchor: input.sanityAnchor ?? null,
    basisNote:
      'Loss ratio priced to the industry benchmark; the recovery is the loss-ratio gap in points applied to reconciled freight revenue.',
  };
}

function normalizeSource(source: {
  label: string;
  asOf?: string | null;
  sourceClassLabel?: string | null;
}): AskHelixNumericDerivationOperandDto['source'] {
  return {
    label: source.label,
    asOf: source.asOf ?? null,
    sourceClassLabel: source.sourceClassLabel ?? null,
  };
}

function trimZeros(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}
